/**
 * Module Loader
 *
 * Loads AOS modules from DAG storage, verifies signatures,
 * and prepares them for sandboxed execution.
 */

import type { DAGStore } from '../dag/store.js';
import {
  type ModuleManifest,
  type ModuleCode,
  type ModulePermission,
  type LoadedModule,
  type PermissionCheckResult,
  PERMISSION_CATEGORIES,
} from './types.js';
import {
  validateManifest,
  verifyModuleManifest,
  checkPermissions,
  parseManifest,
} from './manifest.js';

// ============================================================================
// Trust Store
// ============================================================================

/**
 * Trust anchor - a source of trusted signing keys
 */
export interface TrustAnchor {
  /** Type of trust anchor */
  type: 'builtin' | 'user' | 'organization' | 'url';
  /** Human-readable name */
  name: string;
  /** Trusted public keys (base58) */
  pubkeys: string[];
  /** When this anchor was added */
  added: number;
  /** Optional expiration */
  expires?: number;
}

/**
 * Trust store configuration
 */
export interface TrustStoreConfig {
  /** Built-in trust anchors (e.g., AOS Foundation keys) */
  builtinAnchors?: TrustAnchor[];
  /** Whether to allow user-added trust anchors */
  allowUserAnchors?: boolean;
  /** Minimum signatures required from trusted keys */
  minTrustedSignatures?: number;
}

/**
 * Manages trusted signing keys and revocations
 */
export class TrustStore {
  private anchors: Map<string, TrustAnchor> = new Map();
  private trustedKeys: Set<string> = new Set();
  private revokedKeys: Set<string> = new Set();
  private config: TrustStoreConfig;

  constructor(config: TrustStoreConfig = {}) {
    this.config = {
      allowUserAnchors: true,
      minTrustedSignatures: 1,
      ...config,
    };

    // Add builtin anchors
    if (config.builtinAnchors) {
      for (const anchor of config.builtinAnchors) {
        this.addAnchor(anchor);
      }
    }
  }

  /**
   * Add a trust anchor
   */
  addAnchor(anchor: TrustAnchor): void {
    if (anchor.type === 'user' && !this.config.allowUserAnchors) {
      throw new Error('User trust anchors not allowed');
    }

    const id = `${anchor.type}:${anchor.name}`;
    this.anchors.set(id, anchor);

    for (const pubkey of anchor.pubkeys) {
      this.trustedKeys.add(pubkey);
    }
  }

  /**
   * Remove a trust anchor
   */
  removeAnchor(type: string, name: string): boolean {
    const id = `${type}:${name}`;
    const anchor = this.anchors.get(id);

    if (!anchor) return false;

    this.anchors.delete(id);

    // Rebuild trusted keys (a key might be in multiple anchors)
    this.trustedKeys.clear();
    for (const a of this.anchors.values()) {
      for (const pubkey of a.pubkeys) {
        this.trustedKeys.add(pubkey);
      }
    }

    return true;
  }

  /**
   * Revoke a key (compromised or no longer trusted)
   */
  revokeKey(pubkey: string): void {
    this.revokedKeys.add(pubkey);
    this.trustedKeys.delete(pubkey);
  }

  /**
   * Un-revoke a key
   */
  unrevokeKey(pubkey: string): void {
    this.revokedKeys.delete(pubkey);

    // Re-add to trusted if it's in any anchor
    for (const anchor of this.anchors.values()) {
      if (anchor.pubkeys.includes(pubkey)) {
        this.trustedKeys.add(pubkey);
        break;
      }
    }
  }

  /**
   * Check if a key is trusted (and not revoked)
   */
  isTrusted(pubkey: string): boolean {
    return this.trustedKeys.has(pubkey) && !this.revokedKeys.has(pubkey);
  }

  /**
   * Check if a key is revoked
   */
  isRevoked(pubkey: string): boolean {
    return this.revokedKeys.has(pubkey);
  }

  /**
   * Get all trusted keys
   */
  getTrustedKeys(): Set<string> {
    return new Set(this.trustedKeys);
  }

  /**
   * Get all revoked keys
   */
  getRevokedKeys(): Set<string> {
    return new Set(this.revokedKeys);
  }

  /**
   * Get all trust anchors
   */
  getAnchors(): TrustAnchor[] {
    return Array.from(this.anchors.values());
  }

  /**
   * Get minimum required trusted signatures
   */
  getMinTrustedSignatures(): number {
    return this.config.minTrustedSignatures ?? 1;
  }

  /**
   * Export trust store state (for persistence)
   */
  export(): TrustStoreState {
    return {
      anchors: Array.from(this.anchors.values()),
      revokedKeys: Array.from(this.revokedKeys),
    };
  }

  /**
   * Import trust store state
   */
  import(state: TrustStoreState): void {
    this.anchors.clear();
    this.trustedKeys.clear();
    this.revokedKeys.clear();

    for (const anchor of state.anchors) {
      this.addAnchor(anchor);
    }

    for (const key of state.revokedKeys) {
      this.revokeKey(key);
    }
  }
}

/**
 * Serializable trust store state
 */
export interface TrustStoreState {
  anchors: TrustAnchor[];
  revokedKeys: string[];
}

// ============================================================================
// Module Loader
// ============================================================================

/**
 * Module loader configuration
 */
export interface ModuleLoaderConfig {
  /** Trust store for signature verification */
  trustStore: TrustStore;
  /** DAG store for fetching module content */
  dagStore: DAGStore;
  /** Maximum module size in bytes (default: 10MB) */
  maxModuleSize?: number;
  /** Whether to allow modules with unknown permissions */
  allowUnknownPermissions?: boolean;
  /** Permissions that are never allowed */
  deniedPermissions?: ModulePermission[];
}

/**
 * Result of loading a module
 */
export interface ModuleLoadResult {
  /** Whether loading succeeded */
  success: boolean;
  /** The loaded module (if successful) */
  module?: LoadedModule;
  /** Errors encountered */
  errors: string[];
  /** Warnings (non-fatal) */
  warnings: string[];
  /** Permission check result */
  permissionCheck?: PermissionCheckResult;
}

/**
 * Module loading stage for progress reporting
 */
export type LoadStage =
  | 'fetching'
  | 'parsing'
  | 'validating'
  | 'verifying'
  | 'loading-code'
  | 'checking-permissions'
  | 'complete';

/**
 * Progress callback for module loading
 */
export type LoadProgressCallback = (stage: LoadStage, progress: number) => void;

/**
 * Module Loader - loads and verifies modules from DAG
 */
export class ModuleLoader {
  private config: ModuleLoaderConfig;
  private loadedModules: Map<string, LoadedModule> = new Map();

  constructor(config: ModuleLoaderConfig) {
    this.config = {
      maxModuleSize: 10 * 1024 * 1024, // 10MB
      allowUnknownPermissions: false,
      deniedPermissions: [],
      ...config,
    };
  }

  /**
   * Load a module from its manifest hash
   */
  async load(
    manifestHash: string,
    onProgress?: LoadProgressCallback
  ): Promise<ModuleLoadResult> {
    const errors: string[] = [];
    const warnings: string[] = [];

    try {
      // Stage 1: Fetch manifest from DAG
      onProgress?.('fetching', 0.1);

      const manifestObj = await this.config.dagStore.get(manifestHash);
      if (!manifestObj) {
        return {
          success: false,
          errors: [`Module manifest not found: ${manifestHash}`],
          warnings: [],
        };
      }

      // Stage 2: Parse manifest
      onProgress?.('parsing', 0.2);

      let manifest: ModuleManifest;
      try {
        // The DAG object content should be the manifest
        // Check both the DAG object type and the content type
        const content = manifestObj.content as Record<string, unknown>;

        if (manifestObj.type !== 'aos.module' && content?.type !== 'aos.module') {
          return {
            success: false,
            errors: [`Invalid object type: ${manifestObj.type}, expected aos.module`],
            warnings: [],
          };
        }

        // Content could be the manifest directly or wrapped
        if (content?.type === 'aos.module') {
          manifest = content as unknown as ModuleManifest;
        } else if (typeof manifestObj.content === 'string') {
          manifest = parseManifest(manifestObj.content);
        } else {
          manifest = manifestObj.content as ModuleManifest;
        }
      } catch (err) {
        return {
          success: false,
          errors: [`Failed to parse manifest: ${err instanceof Error ? err.message : 'unknown error'}`],
          warnings: [],
        };
      }

      // Stage 3: Validate manifest structure
      onProgress?.('validating', 0.3);

      const validation = validateManifest(manifest);
      if (!validation.valid) {
        return {
          success: false,
          errors: validation.errors,
          warnings: validation.warnings,
        };
      }
      warnings.push(...validation.warnings);

      // Stage 4: Verify signatures
      onProgress?.('verifying', 0.4);

      const verifyResult = verifyModuleManifest(
        manifest,
        this.config.trustStore.getTrustedKeys(),
        this.config.trustStore.getRevokedKeys()
      );

      if (!verifyResult.valid) {
        return {
          success: false,
          errors: verifyResult.errors,
          warnings,
        };
      }

      // Check minimum trusted signatures
      const minRequired = this.config.trustStore.getMinTrustedSignatures();
      if (verifyResult.trustedSigners.length < minRequired) {
        return {
          success: false,
          errors: [`Insufficient trusted signatures: ${verifyResult.trustedSigners.length}/${minRequired}`],
          warnings,
        };
      }

      // Stage 5: Load code chunks
      onProgress?.('loading-code', 0.5);

      const codeResult = await this.loadCode(manifest, onProgress);
      if (!codeResult.success) {
        return {
          success: false,
          errors: codeResult.errors,
          warnings,
        };
      }

      // Stage 6: Check permissions
      onProgress?.('checking-permissions', 0.8);

      const permissionCheck = checkPermissions(manifest.permissions);

      // Check for denied permissions
      const deniedFound = manifest.permissions.filter(p =>
        this.config.deniedPermissions?.includes(p)
      );
      if (deniedFound.length > 0) {
        return {
          success: false,
          errors: [`Module requests denied permissions: ${deniedFound.join(', ')}`],
          warnings,
          permissionCheck,
        };
      }

      // Check for unknown permissions
      if (!this.config.allowUnknownPermissions && permissionCheck.unknown.length > 0) {
        return {
          success: false,
          errors: [`Module requests unknown permissions: ${permissionCheck.unknown.join(', ')}`],
          warnings,
          permissionCheck,
        };
      }

      // Stage 7: Complete
      onProgress?.('complete', 1.0);

      const loadedModule: LoadedModule = {
        manifest,
        code: codeResult.code!,
        verified: true,
        trustedSigners: verifyResult.trustedSigners,
        grantedPermissions: [], // Will be set after user approval
        loadedAt: Date.now(),
      };

      // Cache the loaded module
      this.loadedModules.set(manifestHash, loadedModule);

      return {
        success: true,
        module: loadedModule,
        errors: [],
        warnings,
        permissionCheck,
      };
    } catch (err) {
      return {
        success: false,
        errors: [`Unexpected error: ${err instanceof Error ? err.message : 'unknown'}`],
        warnings,
      };
    }
  }

  /**
   * Load module code chunks from DAG
   */
  private async loadCode(
    manifest: ModuleManifest,
    onProgress?: LoadProgressCallback
  ): Promise<{ success: boolean; code?: string; errors: string[] }> {
    const errors: string[] = [];
    const chunks: string[] = [];

    // Load entry chunk
    const entryObj = await this.config.dagStore.get(manifest.entry);
    if (!entryObj) {
      return {
        success: false,
        errors: [`Entry code chunk not found: ${manifest.entry}`],
      };
    }

    if (entryObj.type !== 'aos.module.code') {
      return {
        success: false,
        errors: [`Invalid entry type: ${entryObj.type}, expected aos.module.code`],
      };
    }

    const entryCode = entryObj.content as ModuleCode;
    chunks.push(entryCode.code);

    // Load additional chunks
    if (manifest.chunks && manifest.chunks.length > 0) {
      const totalChunks = manifest.chunks.length;
      for (let i = 0; i < totalChunks; i++) {
        const chunkHash = manifest.chunks[i];
        const progress = 0.5 + (0.3 * (i + 1)) / totalChunks;
        onProgress?.('loading-code', progress);

        const chunkObj = await this.config.dagStore.get(chunkHash);
        if (!chunkObj) {
          errors.push(`Code chunk not found: ${chunkHash}`);
          continue;
        }

        if (chunkObj.type !== 'aos.module.code') {
          errors.push(`Invalid chunk type: ${chunkObj.type}`);
          continue;
        }

        const chunkCode = chunkObj.content as ModuleCode;
        chunks.push(chunkCode.code);
      }
    }

    if (errors.length > 0) {
      return { success: false, errors };
    }

    const code = chunks.join('\n');

    // Check size limit
    const maxSize = this.config.maxModuleSize ?? 10 * 1024 * 1024;
    if (code.length > maxSize) {
      return {
        success: false,
        errors: [`Module code exceeds size limit: ${code.length} > ${maxSize}`],
      };
    }

    return { success: true, code, errors: [] };
  }

  /**
   * Get a cached loaded module
   */
  getLoaded(manifestHash: string): LoadedModule | undefined {
    return this.loadedModules.get(manifestHash);
  }

  /**
   * Check if a module is loaded
   */
  isLoaded(manifestHash: string): boolean {
    return this.loadedModules.has(manifestHash);
  }

  /**
   * Unload a module from cache
   */
  unload(manifestHash: string): boolean {
    return this.loadedModules.delete(manifestHash);
  }

  /**
   * Get all loaded modules
   */
  getLoadedModules(): Map<string, LoadedModule> {
    return new Map(this.loadedModules);
  }

  /**
   * Grant permissions to a loaded module
   */
  grantPermissions(
    manifestHash: string,
    permissions: ModulePermission[]
  ): boolean {
    const module = this.loadedModules.get(manifestHash);
    if (!module) return false;

    // Only grant permissions that were requested
    const validPermissions = permissions.filter(p =>
      module.manifest.permissions.includes(p)
    );

    module.grantedPermissions = validPermissions;
    return true;
  }

  /**
   * Check if a module has a specific permission granted
   */
  hasPermission(manifestHash: string, permission: ModulePermission): boolean {
    const module = this.loadedModules.get(manifestHash);
    if (!module) return false;
    return module.grantedPermissions.includes(permission);
  }

  /**
   * Get the trust store
   */
  getTrustStore(): TrustStore {
    return this.config.trustStore;
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Create a simple module loader with default configuration
 */
export function createModuleLoader(
  dagStore: DAGStore,
  trustedKeys?: string[]
): ModuleLoader {
  const trustStore = new TrustStore();

  if (trustedKeys && trustedKeys.length > 0) {
    trustStore.addAnchor({
      type: 'user',
      name: 'default',
      pubkeys: trustedKeys,
      added: Date.now(),
    });
  }

  return new ModuleLoader({
    trustStore,
    dagStore,
  });
}

/**
 * Quickly verify a module manifest without loading code
 */
export function quickVerify(
  manifest: ModuleManifest,
  trustedKeys: Set<string>,
  revokedKeys?: Set<string>
): { valid: boolean; errors: string[] } {
  const validation = validateManifest(manifest);
  if (!validation.valid) {
    return { valid: false, errors: validation.errors };
  }

  const verifyResult = verifyModuleManifest(manifest, trustedKeys, revokedKeys);
  return { valid: verifyResult.valid, errors: verifyResult.errors };
}

/**
 * Get permissions that need user approval for a module
 */
export function getPermissionsNeedingApproval(
  permissions: ModulePermission[]
): { prompt: ModulePermission[]; dangerous: ModulePermission[] } {
  const check = checkPermissions(permissions);
  return {
    prompt: check.prompt,
    dangerous: check.dangerous,
  };
}

/**
 * Check if all requested permissions are safe (no approval needed)
 */
export function areAllPermissionsSafe(permissions: ModulePermission[]): boolean {
  return permissions.every(p => PERMISSION_CATEGORIES[p] === 'safe');
}
